import { useCallback } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'

import { useInView } from '@frontend/hooks/useInView'
import { useGraphQLClient } from '@utils/GraphQLClientProvider'
import { SupporterSmsMessagesDocument } from '@frontend/graphql/queries/supporters.generated'
import { SmsConversation, type SmsConversationPage } from './SmsConversation'
import EmptyState from '@components/molecules/EmptyState'
import { Button } from '@atoms'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('supporter_show')

const PAGE_SIZE = 20

type SmsConversationTabProps = {
  supporterId: string
  groupId: string
  supporterSmsMessages: SmsConversationPage
}

export function SmsConversationTab({ supporterId, groupId, supporterSmsMessages }: SmsConversationTabProps) {
  const client = useGraphQLClient()

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError } = useInfiniteQuery({
    queryKey: ['supporterSmsMessages', supporterId],
    queryFn: async ({ pageParam = 0 }): Promise<SmsConversationPage> => {
      const result = await client.request(SupporterSmsMessagesDocument, {
        group_id: groupId,
        supporter_id: supporterId,
        offset: pageParam,
        first: PAGE_SIZE,
      })
      const page = result.group?.supporters.edges?.[0]?.node?.smsMessages
      if (!page) throw new Error('SMS conversation is unavailable')

      return page
    },
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.nodes.length === 0) return undefined

      const loaded = pages.reduce((count, page) => count + page.nodes.length, 0)
      return loaded < lastPage.totalCount ? loaded : undefined
    },
    initialData: { pages: [supporterSmsMessages], pageParams: [0] },
    staleTime: Infinity,
  })

  const messages = data?.pages.flatMap((page) => page.nodes) ?? []
  const loadOlder = useCallback(() => {
    fetchNextPage()
  }, [fetchNextPage])

  return (
    <div data-testid="sms-conversation-tab" className="tw:flex tw:flex-col tw:gap-3">
      {messages.length === 0 ? (
        <EmptyState
          iconType="message-chat-square"
          headline={t('sms_conversation.empty.title')}
          subtext={t('sms_conversation.empty.subtext')}
        />
      ) : (
        <div
          tabIndex={0}
          className="tw:max-h-[60vh] tw:overflow-y-auto tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:p-4 tw:focus-visible:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-primary-300"
        >
          <SmsConversation
            messages={messages}
            ariaLabel={t('sms_conversation.aria_label')}
            footer={
              <LoadOlderFooter
                hasMore={Boolean(hasNextPage)}
                isLoading={isFetchingNextPage}
                hasError={isError}
                onLoad={loadOlder}
              />
            }
          />
        </div>
      )}
    </div>
  )
}

function LoadOlderFooter({
  hasMore,
  isLoading,
  hasError,
  onLoad,
}: {
  hasMore: boolean
  isLoading: boolean
  hasError: boolean
  onLoad: () => void
}) {
  const sentinelRef = useInView<HTMLDivElement>({ enabled: hasMore && !hasError, onEnter: onLoad })

  if (!hasMore && !isLoading) {
    return (
      <p className="tw:py-2 tw:text-center tw:text-xs tw:text-gray-true-500">{t('sms_conversation.start_reached')}</p>
    )
  }

  return (
    <div ref={sentinelRef} className="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:py-2">
      {isLoading && <SmsConversationSkeleton />}
      {hasError && (
        <p role="alert" className="tw:text-sm tw:text-warning-700">
          {t('sms_conversation.load_error')}
        </p>
      )}
      {hasMore && !isLoading && (
        <Button rank="secondary" size="sm" onClick={onLoad}>
          {hasError ? t('sms_conversation.retry') : t('sms_conversation.load_older')}
        </Button>
      )}
    </div>
  )
}

function SmsConversationSkeleton() {
  return (
    <div data-testid="sms-conversation-skeleton" className="tw:flex tw:w-full tw:flex-col tw:gap-2" aria-hidden>
      <div className="tw:h-10 tw:w-2/3 tw:self-start tw:animate-pulse tw:rounded-2xl tw:bg-gray-100" />
      <div className="tw:h-10 tw:w-1/2 tw:self-end tw:animate-pulse tw:rounded-2xl tw:bg-primary-50" />
    </div>
  )
}
